// oxlint-disable only-throw-error
// oxlint-disable use-unknown-in-catch-callback-variable
import { adminEmail, mimeTypes, searchFields } from '@/shared/constants';
import type { DriveFile, DriveMimeType, Permission } from '@/shared/DriveTypes';
import { type ServerProfile } from '@/shared/types';
import { fuzzy } from 'fast-fuzzy';
import { type PopupState } from 'material-ui-popup-state/hooks';
import { createContext, useContext, useEffect, useEffectEvent, useMemo, useReducer, useRef, useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { serialize, type SWRResponse } from 'swr/_internal';
import useSWRInfinite from 'swr/infinite';
import { useLocation, useRoute } from 'wouter';
import { type DriveFileWithExport, type ExportResult, type Params } from '~/utils/DriveAPI';
import {
  DEV,
  LEGACY,
  MODERN,
  defaultScope,
  expiredToken,
  expires_in_default,
  focusDisabled,
  isBun,
  ms,
  newFileId,
  path,
  rootFolderQuery,
} from '../constants';
import * as DriveAPI from '../DriveAPI';
import { generateQuery } from '../generateQuery';
import { getPrevNextSibling } from '../getPrevNext';
import gup from '../gup';
import { isMarkdownLinkTitle } from '../isMarkdownLink';
import { log } from '../logger';
import { loadOnce } from '../middleware/loadOnce';
import { recursiveFetch } from '../middleware/recursiveFetch';
import { throttle } from '../middleware/throttle';
import { sortByName } from '../sort';
import { useStore, useStreamed } from '../store';
import type { ConnectionsResponse, Contact, KeyCode, Profile } from '../types';
import {
  assertType,
  delay,
  getId,
  getMimeType,
  getNewFile,
  getSharedDriveId,
  getTextFromFile,
  isADocument,
  isAFolder,
  isAPDF,
  isAShortcut,
  isExportable,
  isFileScope,
  isInputEvent,
  ls,
  sortArrByName,
  uniqueBy,
} from '../utils';

/**
 * Avoids unnecessary re-renders due to using spread operator on swr object
 * https://github.com/vercel/swr/pull/890
 */
const assign = <T extends SWRResponse, O extends Record<string, unknown>>(swr: T, obj: O): T & O => {
  const descriptors: Partial<Record<keyof O, PropertyDescriptor>> = {};

  for (const [key, value] of Object.entries(obj)) {
    descriptors[key] = { value, enumerable: true };
  }

  return Object.defineProperties(swr, descriptors as Record<keyof O, PropertyDescriptor>) as T & O;
};

type SearchResultEntry = DriveFile & {
  content: string;
  nameLower: string;
  nameStarts: boolean;
  nameIncludes: boolean;
  contentIncludes: boolean;
};

type SearchEntry = DriveFile | (DriveFile & { content: string }) | DriveAPI.DriveFileWithExport;

/**
 * used by App to call the network on mount/refresh interval
 */
export const useAuthLoader = () => {
  const swr = useSWR<Profile | undefined, unknown>(
    '/api/user/me',
    async (url: string) => {
      log.info('GET: /user/me');
      const res = await fetch(url, { priority: 'high' });
      const user = await res.json<Profile | undefined>();
      if (!res.ok) {
        assertType<{ message: string; statusText: string }>(user);
        log.error('Failed to fetch user');
        throw Error(`Failed to fetch user: ${user.message}`, { cause: user.statusText });
      }
      log.info('user', user);
      if (!user) return undefined;
      if (user.tokens) {
        user.tokens.expires_at = Date.now() + (user.tokens.expires_in ?? expires_in_default) * ms;
      }
      return user;
    },
    {
      revalidateOnMount: true,
      refreshInterval: (user) => {
        if (!user?.tokens?.expires_at) return 0;
        const expiresIn = user.tokens.expires_at - Date.now();
        if (expiresIn < 0) return 0;
        return expiresIn;
      },
    }
  );

  const { data: user } = swr;
  if (user?.tokens) {
    if (typeof gapi === 'undefined' || gapi.client?.setToken === undefined) {
      return assign(swr, {
        isLoading: true,
        anonymous: false,
        authorized: false,
      });
    }
    gapi.client.setToken({
      access_token: user.tokens.access_token,
      expires_in: user.tokens.expires_in,
      scope: user.tokens.scope,
    });
    return assign(swr, {
      anonymous: false,
      authorized: true,
    });
  }

  return assign(swr, {
    anonymous: true,
    authorized: false,
  });
};

/**
 * used for accessing the auth details without having to make a request
 * tests were written using the fetch endpoint, so we are using it here
 */
export let useAuth = isBun
  ? useAuthLoader
  : () => {
      const swr = useSWR<Profile | undefined, unknown>('/api/user/me', { revalidateOnMount: false });

      const { data: user } = swr;
      if (user?.tokens) {
        const expired = Date.now() > (user.tokens.expires_at ?? 0);
        if (expired || typeof gapi === 'undefined' || gapi.client?.setToken === undefined) {
          return assign(swr, {
            isLoading: true,
            anonymous: false,
            authorized: false,
          });
        }
        return assign(swr, {
          anonymous: false,
          authorized: true,
        });
      }

      return assign(swr, {
        anonymous: true,
        authorized: false,
      });
    };

// The legacy token refresh is handled by the gapi client
if (import.meta.env.VITE_LEGACY) {
  // @ts-expect-error - type is the same shape, but it does not return the swr object
  useAuth = (): { data: ServerProfile | undefined; anonymous: boolean; authorized: boolean; isLoading: boolean } => {
    const unauthorized = { anonymous: Boolean(gup('p')), isLoading: false, authorized: false, data: undefined };
    if (typeof gapi === 'undefined' || !gapi.auth2) return { ...unauthorized, isLoading: true };
    const authInstance = gapi.auth2.getAuthInstance();
    if (!authInstance) return unauthorized;
    const currentUser = gapi.auth2.getAuthInstance().currentUser.get();
    const profile = currentUser.getBasicProfile();
    if (!profile) return unauthorized;
    const auth = currentUser.getAuthResponse();

    const data = {
      id: profile.getId(),
      name: profile.getName(),
      email: profile.getEmail(),
      image: profile.getImageUrl(),
      tokens: {
        access_token: auth.access_token,
        id_token: auth.id_token,
        expires_in: auth.expires_in,
        scope: auth.scope,
      },
    } as const satisfies ServerProfile;

    return {
      isLoading: false,
      anonymous: false,
      authorized: Boolean(data.tokens.access_token),
      data,
    };
  };
}

export const useRootFolder = () => {
  const { authorized, isLoading: isAuthLoading } = useAuth();
  const query = rootFolderQuery;
  const swr = useSWR<DriveFile | undefined, unknown>(
    authorized ? `/drive/v3/files?q=${query}` : null,
    async () => {
      if (!query) return Promise.reject(new Error('No query provided'));
      const folders = await DriveAPI.list({ query, fields: searchFields });
      // create YNAW folder if it doesn't exist
      if (MODERN && folders.length === 0) {
        const params = {
          supportsAllDrives: true,
          includeItemsFromAllDrives: true,
        } as const;
        const { result: folder } = await gapi.client
          .request({
            path: 'https://www.googleapis.com/drive/v3/files',
            method: 'POST',
            body: {
              name: 'YNAW',
              mimeType: mimeTypes.folder,
            },
            params,
          })
          .getPromise();
        await gapi.client
          .request({
            path: `https://www.googleapis.com/drive/v3/files/${folder?.id}/permissions`,
            method: 'POST',
            body: {
              role: 'writer',
              emailAddress: adminEmail,
              type: 'user',
            },
            params,
          })
          .getPromise();

        return folder;
      }
      return folders.at(0);
    },
    {
      use: [loadOnce],
    }
  );
  return assign(swr, {
    isLoading: isAuthLoading || swr.isLoading,
  });
};

const filesLoaded = Promise.withResolvers();
const isPagePath = (path: string) => path === '/' || path === '/app' || path.startsWith('/app/page');

export const useWikis = () => {
  const { cache } = useSWRConfig();
  const { data: user } = useAuth();
  const { data: folder, isLoading: isLoadingRootFolder } = useRootFolder();
  const id = folder?.id;
  const userWikiIds = user?.wikis;
  const query = id
    ? `'${id}' in parents and (mimeType = '${mimeTypes.folder}' or mimeType = '${mimeTypes.shortcut}' and shortcutDetails.targetMimeType = '${mimeTypes.folder}')`
    : undefined;

  const swr = useSWR<DriveFile[]>(
    query ? `/drive/v3/files?q=${query}` : null,
    async () => {
      // log.dev('swr: start');
      if (!query) return Promise.reject(new Error('No query provided'));
      if (isPagePath(path)) {
        await Promise.race([filesLoaded.promise, delay(1000)]); // delaying improves load performance of the current wiki
      }
      const files = await DriveAPI.list({ query });
      const folders = files.filter((o) => o.mimeType === mimeTypes.folder);
      const shortcuts = files.filter((o) => isAShortcut(o));
      const requests = shortcuts.map((o) =>
        DriveAPI.get({ id: o.shortcutDetails.targetId }).then((file) => ({ ...file, shortcutId: o.id }) as DriveFile)
      );
      const userWikiRequests = (userWikiIds ?? []).map((wikiId) => DriveAPI.get({ id: wikiId }));
      const [shortcutResponses, userWikiResponses] = await Promise.all([
        Promise.allSettled(requests),
        Promise.allSettled(userWikiRequests),
      ]);

      const shortcutValues = shortcutResponses
        .map((o) => ('value' in o ? o.value : undefined))
        .filter((o): o is DriveFile => !!o);
      const userWikiValues = userWikiResponses
        .map((o) => ('value' in o ? o.value : undefined))
        .filter((o): o is DriveFile => !!o);

      const allWikis = [...folders, ...shortcutValues, ...userWikiValues];
      const wikis = sortArrByName(uniqueBy(allWikis, 'id'));
      // Cache each wiki
      for (const wiki of wikis) {
        cache.set(`${keys.file}${wiki.id}`, { data: wiki });
      }
      // log.dev('swr: end');
      return wikis;
    },
    {
      use: [loadOnce],
      keepPreviousData: true,
    }
  );

  if (DEV && swr.data?.length) {
    log.once(id, 'wikis', swr.data);
  }

  // log.dev('swr:', {
  //   user,
  //   folder,
  //   isLoading: (isLoadingRootFolder && !folder) || swr.isLoading,
  // });

  return assign(swr, {
    data: swr.data ?? [],
    isLoading: (isLoadingRootFolder && !folder) || swr.isLoading,
  });
};

export const useWikisMutate = () => {
  const { data: folder } = useRootFolder();
  const query = folder?.id
    ? `'${folder.id}' in parents and (mimeType = '${mimeTypes.folder}' or mimeType = '${mimeTypes.shortcut}' and shortcutDetails.targetMimeType = '${mimeTypes.folder}')`
    : undefined;
  return useSWR<DriveFile[]>(query ? `/drive/v3/files?q=${query}` : null, null, { revalidateOnMount: false });
};

/**
 * recursively fetches all files in the wiki and saves to the swr cache once completed
 * saving to a single key `/api/files/:wikiId` was chosen over caching every /drive/v3 request
 */
export const useFiles = (wikiId: string | undefined | null) => {
  const [streamed, setStreamed] = useStreamed();
  const email = useAuth().data?.email;
  const idRef = useRef<string | undefined | null>(wikiId);
  const swr = useSWR(
    wikiId
      ? {
          // recursiveFetch saves `key` property as the cache key:
          // e.g. useSWR('/api/files/0')
          key: `${keys.files}${wikiId}`,
          id: wikiId,
          email,
        }
      : null,
    (data: DriveFile[]): DriveFile[] => {
      setStreamed(data);
      return data;
    },
    {
      use: [recursiveFetch, loadOnce],
    }
  );

  // If the wiki id changes we cancel streaming
  useEffect(() => {
    if (wikiId !== idRef.current) {
      idRef.current = wikiId;
      setStreamed([]);
    }
  }, [wikiId, setStreamed]);

  const data = swr.data ?? streamed;
  const files = data.filter((file) => file.name !== 'wiki.logo' && file.name !== 'wiki.page');

  // Logging
  if (DEV && swr.data?.length) {
    log.once(wikiId, 'files', data);
    log.once(
      wikiId,
      'indexes',
      data.map((o) => ({ name: o.name, index: o.properties?.index, mimeType: o.mimeType, parent: o.parents?.[0] }))
    );
  }
  if (!swr.isLoading) filesLoaded.resolve();
  return assign(swr, { data, files });
};

export const keys = {
  file: `/drive/v3/files/`,
  files: '/api/files/',
  user: '/api/files/user/',
  export: '/drive/v3/files/{id}/export',
};

// Shorthand for mutation/cache key, without all the other operations running
export const useMutateFiles = (wikiId: string | undefined) => useSWR(wikiId ? `${keys.files}${wikiId}` : null);
export const useMutateFile = (id: string | undefined) => useSWR(`${keys.file}${id}`);

export const useUserFilesData = () => {
  const { data: user } = useAuth();
  return useSWR<DriveFile[]>(user?.id ? `${keys.user}${user.id}` : null);
};
let userFilesInit = false;
export const useUserFiles = () => {
  const { data: user } = useAuth();
  const { cache } = useSWRConfig();
  return useSWR(user?.id ? `${keys.user}${user.id}` : null, async () => {
    if (userFilesInit && user) {
      const files = cache.get(`${keys.user}${user.id}`)?.data as DriveFile[] | undefined;
      if (files) return files;
    }
    userFilesInit = true;
    await Promise.race([filesLoaded.promise.then(() => delay(1000)), delay(3500)]);
    const { result } = await gapi.client
      .request({
        path: 'https://www.googleapis.com/drive/v3/files',
        params: {
          q: `trashed=false and 'me' in owners`,
          fields: 'nextPageToken, files(id)',
          includeItemsFromAllDrives: true,
          supportsAllDrives: true,
          pageSize: 1000,
          orderBy: 'modifiedTime desc',
        },
      })
      .getPromise()
      .catch(() => ({ result: { files: [] as DriveFile[] } }));
    log.once('userFiles', 'userFiles', result.files.length);
    return result.files;
  });
};

const timers = new Map<string, NodeJS.Timeout>();
const revalidateKey = 'skip-revalidate';
const skipRevalidate = (s: unknown): s is { revalidate: false } => {
  const flag = s !== null && typeof s === 'object' && 'revalidate' in s && s.revalidate === false;
  if (flag) {
    clearTimeout(timers.get(revalidateKey));
    timers.set(
      revalidateKey,
      setTimeout(() => {
        history.replaceState(null, document.title, window.location.href);
        timers.delete(revalidateKey);
      }, 0)
    );
  }
  return flag;
};

export const useFile = (id: string | undefined, files: DriveFile[], wikiId?: string, getShortcut = false) => {
  const isNewFile = id?.startsWith(newFileId);
  const { cache, mutate } = useSWRConfig();
  const navigate = useLocation()[1];
  const file = id && id !== wikiId ? files?.find((o) => o.id === id) : undefined;
  const swr = useSWR<DriveFile | undefined, unknown>(
    id ? (isNewFile ? null : `/drive/v3/files/${id}`) : null,
    async () => {
      if (skipRevalidate(history.state)) return;
      if (!id) return undefined;
      const result = await DriveAPI.get({ id });
      if (isAShortcut(result)) {
        const res = await DriveAPI.get({
          id: result.shortcutDetails.targetId,
          fields: 'size,permissions',
        });
        result.size = res.size;
        if (res.permissions) {
          result.fileDetails = {
            permissions: res.permissions,
          };
        }
      }
      if (skipRevalidate(history.state)) return;

      // If file is trashed, remove it from the files list
      if (wikiId && result?.trashed) {
        const cachedFiles = (cache.get(`${keys.files}${wikiId}`)?.data as DriveFile[] | undefined) ?? [];
        const nextFiles = cachedFiles.filter((f) => f.id !== id);
        if (nextFiles.length < cachedFiles.length) {
          void mutate(`${keys.files}${wikiId}`, nextFiles, false);
          if (window.location.pathname === `/app/page/${wikiId}/${id}`) {
            const file = cachedFiles.find((o) => o.id === id);
            const { next } = file ? getPrevNextSibling({ file, files }) : {};
            navigate(`/app/page/${wikiId}/${next?.id ?? file?.parents?.[0] ?? wikiId}`);
          }
        }
      }
      return result;
    },
    {
      use: [throttle],
      revalidateOnFocus: !!file?.capabilities?.canEdit && id !== wikiId && !isAShortcut(file),
      errorRetryCount: 0,
    }
  );
  const { data } = swr;

  // handles fetching shortcut file
  const isShortcut = id && getShortcut && isAShortcut(data) ? data.shortcutDetails.targetId : null;
  const target = useSWR<DriveFile | undefined, unknown>(isShortcut, () => (id ? DriveAPI.get({ id }) : undefined), {
    use: [throttle],
  });

  // Logging
  if (DEV) {
    const path = window.location.pathname;
    const pathId = path.split('/').at(-1);
    const _file = data ?? file;
    if (_file && (_file?.id === pathId || path === '/')) {
      log.once(_file.id, 'file', _file);
    }
  }

  if (target.data) return target;
  if (data && !data.parents && file?.parents) {
    return assign(swr, {
      isLoading: false,
      data: {
        ...data,
        parents: file.parents,
      },
    });
  }

  // wait for data to resolve for shortcuts (file.size and permissions)
  const result = data ?? (isAShortcut(file) ? undefined : file);
  return assign(swr, {
    isLoading: result ? false : swr.isLoading,
    data: result,
  });
};

let lastExportId: string | undefined; // [legacy] if lastExportId is the same as file.id we can assume this is a focus revalidate

export const useExport = (
  file: DriveFile | undefined,
  legacyHTML?: string // [legacy] allows skipping a double request to html export
) => {
  const id = file ? getId(file) : null;
  const mimeType = getMimeType(file);
  const swr = useSWR(
    id ? `/drive/v3/files/${id}/export` : null,
    async () => {
      if (!id) throw new Error('No id provided');
      if (!mimeType) throw new Error('No mimeType provided');
      if (skipRevalidate(history.state)) return;
      if (file && isExportable(file)) {
        const result = await DriveAPI.exportFile({
          id,
          file,
          ...(LEGACY && lastExportId !== id && { legacyHTML }),
        });
        if (LEGACY && legacyHTML) lastExportId = id;

        return result;
      }
      return {
        html: '',
      };
    },
    {
      use: [throttle],
      focusThrottleInterval: 3000,
      ...(file?.capabilities?.canEdit &&
        !isAPDF(file) &&
        !focusDisabled && {
          revalidateOnFocus: true,
        }),
    }
  );
  return swr;
};

/**
 * Gets exports for all files provided
 */
export const useExports = (
  files: DriveFile[] | undefined
): { data: DriveAPI.DriveFileWithExport[]; isLoading: boolean } => {
  const swr = useSWRInfinite(
    (pageIndex) => {
      if (!files?.length) return null;
      const file = files[pageIndex];
      if (!file) return null;
      return `/drive/v3/files/${file.id}/export`;
    },
    async (args) => {
      const id = args.split('/').at(-2);
      if (!id) throw new Error('No id provided');
      const file = files?.find((o) => o.id === id);
      const mimeType = getMimeType(file);
      if (!mimeType) throw new Error('No mimeType provided');
      if (file && isExportable(file)) {
        return DriveAPI.exportFile({ id, file });
      }
      return {
        html: '',
      };
    },
    {
      initialSize: files?.length ?? 0,
      revalidateAll: true,
      parallel: true,
    }
  );
  const { data } = swr;
  if (!data) return assign(swr, { data: [] });

  const result =
    files?.map((file, i) => {
      const d = data[i];
      return {
        ...file,
        data: d ?? {
          html: '',
        },
      };
    }) ?? [];

  return assign(swr, {
    data: result,
  });
};

export const useCachedExports = (files: DriveFile[]): DriveFileWithExport[] => {
  const { cache } = useSWRConfig();

  const data = files
    .filter(isExportable)
    .map((file) => {
      const id = getId(file);
      if (!id) {
        window.TrackJS && TrackJS.console.log(file);
        window.TrackJS && TrackJS.track('useCachedExports - file does not contain id');
        return {
          ...file,
          data: {
            html: '',
          },
        };
      }
      const path = serialize(`/drive/v3/files/${id}/export`)?.[0];
      const data = cache.get(path)?.data as ExportResult | undefined;

      return {
        ...file,
        data,
      };
    })
    // If the export returns an error we cache an empty string to prevent the request from being made again
    // Which is why we're checking for a result or empty string here
    .filter((o) => o.data?.html || o.data?.html === '') as DriveFileWithExport[];

  return data;
};

/**
 * Immediately sets initial value, but debounces setting it to undefined by a specified delay.
 */
export function useLeadingDebounceTruthy<T>(value: T, delay = 100): T | undefined {
  const [debouncedValue, setDebouncedValue] = useState<T | undefined>(value);

  useEffect(() => {
    if (value) {
      queueMicrotask(() => setDebouncedValue(value));
    } else {
      const handler = setTimeout(() => {
        setDebouncedValue(undefined);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }
    return;
  }, [value, delay]);

  if (isBun) return value;
  return debouncedValue;
}

/**
 * Debounces an initial value but immediately sets it on the first call
 */
export function useLeadingDebounce<T>(value: T, delay = 100): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const timerRef = useRef<Timer>(undefined);
  const firstValue = useRef<T>(value);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (value !== debouncedValue) {
      if (value === firstValue.current) {
        queueMicrotask(() => setDebouncedValue(value));
        return;
      }

      timerRef.current = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
    }

    return () => clearTimeout(timerRef.current);
  }, [value, delay, debouncedValue]);

  return debouncedValue;
}

/**
 * Returns search results based on query
 * @param input - Search query
 * @param wiki - The wiki folder
 * @param files - All files in the wiki
 * @param exportLimit - Limit the number of export requests
 */
export const useSearch = (
  input: string,
  wiki: DriveFile,
  files: DriveFile[]
): { data: SearchResultEntry[]; isLoading: boolean } => {
  const { data: user } = useAuth();
  const lowerInput = input.toLowerCase();
  const folders = [wiki].concat(files.filter(isAFolder));
  const query =
    input.length > 2 ? generateQuery({ files: folders, wiki, email: user?.email, isTextSearch: true }) : undefined;
  const prefix = '/drive/v3/files?q=';
  const {
    data: results,
    isLoading: isLoadingResults,
    isValidating: isValidatingResults,
  } = useSWRInfinite(
    (pageIndex) => {
      if (!query) return null;
      const q = typeof query === 'string' ? query : query[pageIndex];
      if (!q) return null;
      const str = `${prefix}fullText contains '${lowerInput}' and ${typeof query === 'string' && !query.startsWith('(') ? `(${q})` : q}`;
      return str;
    },
    async (args) => {
      const query = args.replace(prefix, '');
      const driveId = getSharedDriveId(folders);
      const data = (await DriveAPI.search({
        q: query,
        fields: isFileScope() ? searchFields : 'files(id,name)',
        ...(driveId && { driveId }),
      }).catch(() => [])) as { id: string }[];
      return data ?? [];
    },
    {
      revalidateAll: true,
      initialSize: query ? (typeof query === 'string' ? 1 : query.length) : 0,
      parallel: true,
    }
  );

  const ids = new Set(results?.flatMap((o) => o.map((r) => r.id)));
  const searchResults = files.filter((o) => ids.has(o.id));
  // only export a file if it's in the search results
  // run a check against cached files as a bonus
  const { data: _searchFiles, isLoading: isLoadingExports } = useExports(searchResults);
  const searchFiles = _searchFiles
    .map((o) => ({ ...o, content: getTextFromFile(o) }))
    .filter((o) => o.content.includes(lowerInput));

  const cachedFilesWithHTML = useCachedExports(files)
    .map((file) => ({
      ...file,
      content: getTextFromFile(file),
    }))
    .filter((file) => file.content.includes(lowerInput));

  const nameFiles = files.filter((file) => {
    const nameLower = file.name.toLowerCase();
    return nameLower.includes(lowerInput) || fuzzy(lowerInput, nameLower, { ignoreSymbols: true }) >= 0.9;
  });

  const seenNames = new Set<string>();
  const data: SearchResultEntry[] =
    input.length === 0
      ? []
      : uniqueBy([...cachedFilesWithHTML, ...searchFiles, ...nameFiles], 'id')
          .map((o: SearchEntry) => {
            const markdown = isADocument(o) ? isMarkdownLinkTitle(o.name) : undefined;
            const nameLower = markdown ? markdown.name.toLowerCase() : o.name.toLowerCase();
            const content = 'content' in o ? o.content : getTextFromFile(o);
            return {
              ...o,
              nameLower,
              content,
              nameStarts: nameLower.startsWith(lowerInput),
              nameIncludes: nameLower.includes(lowerInput),
              contentIncludes: content.includes(lowerInput),
            };
          })
          .filter((o) => {
            // If links have the same file name: [link](url) they are the same entry
            // filter them to avoid duplicates
            if (!isADocument(o) || !isMarkdownLinkTitle(o.name)) return true;
            if (seenNames.has(o.name)) return false;
            seenNames.add(o.name);
            return true;
          })
          .sort((a, b) => {
            if (a.nameStarts !== b.nameStarts) return a.nameStarts ? -1 : 1;
            if (a.nameIncludes !== b.nameIncludes) return a.nameIncludes ? -1 : 1;
            if (a.nameIncludes && a.contentIncludes !== b.contentIncludes) return a.contentIncludes ? -1 : 1;
            if (a.contentIncludes && !b.nameIncludes && !b.contentIncludes) return -1;
            if (b.contentIncludes && !a.nameIncludes && !a.contentIncludes) return 1;
            return sortByName(a, b);
          });

  const isLoadingValue = useLeadingDebounceTruthy(isLoadingResults || isLoadingExports || isValidatingResults, 200);
  const isLoading = input.length === 0 ? false : isLoadingValue;
  return {
    data,
    isLoading: Boolean(isLoading),
  };
};

// https://github.com/uidotdev/usehooks/blob/experimental/index.js#L757
export function useKeyPress(keys: KeyCode | KeyCode[], cb: (e: KeyboardEvent) => void) {
  const onListen = useEffectEvent(cb);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (isInputEvent(event)) return;
      if (Array.isArray(keys) ? keys.includes(event.key as KeyCode) : keys === event.key) {
        onListen(event);
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [keys]);
}

export function useKeyPressRaw(
  keys: KeyCode | KeyCode[],
  cb: (e: KeyboardEvent) => void,
  isInputEventhandler: typeof isInputEvent
) {
  const onListen = useEffectEvent(cb);
  const isInputEventCb = useEffectEvent(isInputEventhandler);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!isInputEventCb(event)) return;
      if (Array.isArray(keys) ? keys.includes(event.key as KeyCode) : keys === event.key) {
        onListen(event);
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [keys]);
}

export const useIsVisible = <T extends React.RefObject<HTMLElement | null>>(ref: T, cb: () => void): void => {
  const fn = useEffectEvent(cb);
  useEffect(() => {
    const target = ref.current;
    if (!window.IntersectionObserver) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          fn();
          if (target) observer.unobserve(target);
        }
      });
    });
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [ref]);
};

/**
 * This is a hook to allow testing invalidating the token while the user is browsing
 */
export const useSimulateExpiredToken = (delay = 3000) => {
  const { cache } = useSWRConfig();
  // Simulate expired token
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  const timerRef = useRef<Timer>(undefined);

  useEffect(() => {
    log.info('simulating expired token in', delay / 1000, 'seconds');
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      log.info('simulating expired token');
      cache.set('/api/user/me', {
        data: {
          tokens: {
            access_token: 'expired',
            expires: 0,
            expiresAt: 0,
          },
        },
      });
      gapi.client.setToken({ access_token: 'expired', expires_in: 0, scope: defaultScope });
      forceUpdate();
    }, delay);
  }, [cache, delay]);
};

if (DEV && expiredToken) {
  gapi.client.setToken({
    access_token: 'expired',
    expires_in: 0,
    scope: defaultScope,
  });
}

export const useBeforeUnload = (callback: () => void) => {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const handler = () => {
      callbackRef.current();
    };
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
    };
  }, []);
};

/**
 * Converts link in page content to use internal navigation
 * Otherwise these links would refresh the page
 */
export const useInternalNavigation = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const navigate = useLocation()[1];
  const handleClick = useEffectEvent((e: Event) => {
    const { target } = e;
    if (!pageRef.current || !target || !(target instanceof HTMLAnchorElement) || !target.href) return;
    const main = pageRef.current.querySelector('main');
    if (!main || !main.contains(target)) return;
    const { href } = target;
    const url = new URL(href);
    const isInternal =
      url.hostname === window.location.hostname || url.hostname === 'localhost' || url.hostname === 'youneedawiki.com';
    if (!isInternal) return;
    e.preventDefault();
    const path = href.replace(url.origin, '');
    log.info('internal navigation to:', path);
    navigate(path);
  });

  useEffect(() => {
    // use pageRef if available, we check again in handleClick regardless
    const el = pageRef.current ?? document;
    el.addEventListener('click', handleClick);
    return () => el.removeEventListener('click', handleClick);
  }, []);
  return pageRef;
};

// Gets wiki id and file id
export const useParams = () => {
  const { data: wikis } = useWikis();
  const route = useRoute('/app/page/:wikiId/:pageId?')[1] ?? {
    pageId: undefined,
    wikiId: undefined,
  };
  const lastWikiId = useMemo(() => (route.wikiId ? undefined : ls.get<string>('current-wiki-id')), [route.wikiId]);
  const validLastWikiId = lastWikiId && wikis?.some((w) => w.id === lastWikiId) ? lastWikiId : undefined;
  const wikiId = route.wikiId || validLastWikiId || wikis?.[0]?.id;

  // support for legacy routes /app/page/:id?p=wikiId
  const p = route.pageId ? undefined : gup('p');
  const params =
    route.pageId || !p || typeof p !== 'string'
      ? {
          pageId: route.pageId,
          wikiId: wikiId,
        }
      : {
          pageId: wikiId,
          wikiId: p,
        };

  return params;
};

const filterDeletedPermission = (p: Permission) => !(p.type === 'user' && p.deleted);

export const usePermissions = (file: DriveFile | undefined) => {
  const swr = useSWR<Permission[]>(file?.capabilities?.canEdit ? `/drive/v3/files/${file.id}/permissions` : null, () =>
    file
      ? (file?.permissions?.filter(filterDeletedPermission) ??
        DriveAPI.permissions
          .list({ id: file.id, fields: '*' })
          .then((permissions) => permissions.filter(filterDeletedPermission)))
      : []
  );
  if (file?.permissions) {
    return assign(swr, {
      data: file.permissions.filter(filterDeletedPermission),
    });
  }
  return swr;
};

export const useContacts = (enabled: boolean) => {
  const token = typeof gapi !== 'undefined' ? gapi.client.getToken()?.access_token : undefined;
  return useSWR<Contact[]>(enabled && token ? `/api/connections` : null, async () => {
    const res = await fetch(
      `https://people.googleapis.com/v1/people/me/connections?sortOrder=LAST_MODIFIED_ASCENDING&pageSize=1000&personFields=emailAddresses%2Cnames%2cphotos&access_token=${token}`
    );
    const data = await res.json<ConnectionsResponse>();
    if (!('connections' in data)) return [];

    // Filter out contacts without email addresses
    const contacts: Contact[] = data.connections
      .filter((o) => o.emailAddresses)
      .map((o) => ({
        id: o.resourceName,
        name:
          o.names?.find((o) => o.metadata.primary)?.displayName ??
          o.emailAddresses?.find((o) => o.metadata.primary)?.value,
        email: o.emailAddresses?.find((o) => o.metadata.primary)?.value,
        photoURL: o.photos?.find((o) => o.metadata.primary)?.url,
      }));

    return contacts;
  });
};

export const useMutatePermission = ({
  file,
  files,
  wiki,
}: {
  file: DriveFile | undefined;
  files: DriveFile[];
  wiki: DriveFile;
}) => {
  const { mutate } = useSWRConfig();
  const { mutate: mutateFiles } = useSWR(`/api/files/${wiki.id}`);
  const { mutate: mutatePermissions } = usePermissions(file);
  const setStatus = useStore((state) => state.setStatus);

  return {
    createPermission: async (permissions: Permission | Permission[], notify = false) => {
      if (!file) throw new Error('File not provided');
      const pendingPermissions = Array.isArray(permissions) ? permissions : [permissions];
      return mutate<DriveFile>(
        `/drive/v3/files/${file.id}`,
        async () => {
          const nextFiles = files.map((f) =>
            f.id === file.id ? { ...f, permissions: [...(f.permissions ?? []), ...pendingPermissions] } : f
          );
          const nextPermissions = file.permissions ? [...file.permissions, ...pendingPermissions] : [];

          // optimistic cache update
          void mutateFiles(nextFiles, false);
          void mutatePermissions(nextPermissions, false);

          setStatus('loading');
          const res = await Promise.allSettled(
            pendingPermissions.map((permission) =>
              DriveAPI.permissions.create({
                fileId: file.id,
                role: permission.role,
                type: permission.type,
                sendNotificationEmail: notify,
                ...('emailAddress' in permission && {
                  emailAddress: permission.emailAddress,
                }),
              })
            )
          );

          const result = res.filter((r) => r.status === 'fulfilled').map((r) => r.value);
          const err = res.find((r) => r.status === 'rejected') as
            | {
                reason: {
                  result: {
                    error: {
                      message: string;
                    };
                  };
                };
              }
            | undefined;

          if (err) {
            // Undo optimistic cache update
            void mutateFiles(files, false);
            void mutatePermissions(file.permissions, false);
            setStatus('error', { message: err.reason.result.error.message });
            throw new Error(err.reason.result.error.message);
          }

          setStatus('idle');
          return {
            ...file,
            permissions: [...(file.permissions ?? []), ...result],
          };
        },
        {
          populateCache: true,
          revalidate: false,
          optimisticData: {
            ...file,
            permissions: [...(file.permissions ?? []), ...pendingPermissions],
          },
        }
      );
    },
    updatePermission: async (permission: Permission) => {
      if (!file) throw new Error('File not provided');
      return mutate<DriveFile>(
        `/drive/v3/files/${file.id}`,
        async () => {
          const nextFiles = files.map((f) =>
            f.id === file.id
              ? {
                  ...f,
                  permissions:
                    f.permissions?.map((p) => (p.id === permission.id ? { ...p, role: permission.role } : p)) ?? [],
                }
              : f
          ) satisfies DriveFile[];
          const nextPermissions = (file.permissions?.map((p) =>
            p.id === permission.id ? { ...p, role: permission.role } : p
          ) ?? []) satisfies Permission[];

          void mutateFiles(nextFiles, false);
          void mutatePermissions(nextPermissions, false);

          setStatus('loading');
          const result = await DriveAPI.permissions
            .update({
              fileId: file.id,
              permissionId: permission.id,
              role: permission.role,
              driveId: file.driveId,
            })
            .finally(() => {
              setStatus('idle');
            })
            .catch(() => {
              setStatus('error', { message: 'Error updating permission' });
              throw new Error('Error updating permission');
            });

          const fileResult = {
            ...file,
            permissions:
              file.permissions?.map((permission) =>
                permission.id === result.id ? { ...permission, role: result.role } : permission
              ) ?? [],
          };
          return fileResult;
        },
        {
          populateCache: true,
          revalidate: false,
          optimisticData: {
            ...file,
            permissions:
              file.permissions?.map((p) => (p.id === permission.id ? { ...p, role: permission.role } : p)) ?? [],
          },
        }
      );
    },
    removePermission: async (permission: Permission) => {
      if (!file) throw new Error('File not found');
      return mutate<DriveFile>(
        `/drive/v3/files/${file.id}`,
        async () => {
          const nextFiles = files.map((f) =>
            f.id === file.id ? { ...f, permissions: f.permissions?.filter((p) => p.id !== permission.id) } : f
          );
          const nextPermissions = file.permissions?.filter((p) => p.id !== permission.id) ?? [];
          void mutateFiles(nextFiles, false);
          void mutatePermissions(nextPermissions, false);

          setStatus('loading');
          await DriveAPI.permissions
            .delete({
              fileId: file.id,
              permissionId: permission.id,
              driveId: file.driveId,
            })
            .finally(() => setStatus('idle'))
            .catch(
              (
                err:
                  | { status: string; reason?: { message: string; stack: string } }
                  | { message: string; stack: string }
              ) => {
                if ('reason' in err) {
                  setStatus('error', { message: err.reason.message });
                } else if ('message' in err) {
                  setStatus('error', { message: err.message });
                }
                throw err;
              }
            );

          return {
            ...file,
            permissions: file.permissions?.filter((p) => p.id !== permission.id) ?? [],
          };
        },
        {
          populateCache: true,
          revalidate: false,
          optimisticData: {
            ...file,
            permissions: file.permissions?.filter((p) => p.id !== permission.id) ?? [],
          },
        }
      );
    },
  };
};

// Global mutate wrapper for DriveAPI
export const useMutateDriveFile = () => {
  const navigate = useLocation()[1];
  const { data: user } = useAuth();
  const { wikiId } = useParams();
  const { data: files } = useFiles(LEGACY ? null : wikiId);
  const { mutate } = useSWRConfig();
  const setNewFile = useStore((state) => state.setNewFile);

  return <K extends 'create'>(
    key: K,
    params: K extends 'create'
      ? Params['create'] & {
          shortcutDetails?: {
            targetId: string;
            targetMimeType?: DriveMimeType;
          };
        }
      : never
  ) => {
    if (!wikiId) throw new Error('No wiki provided');
    if (key === 'create') {
      const { parents } = params;
      const newFile = getNewFile({
        ...params,
        user,
      });
      setNewFile(newFile);

      const request = DriveAPI.create({
        ...params,
        parents,
      })
        .catch(async (err: { result: { error: { code: number; status: string; message: string } } } | Error) => {
          if (err instanceof Error) throw err;
          const { error } = err.result;
          if (error.code === 500 && error.status === 'UNKNOWN') {
            const file = await DriveAPI.getLast({ driveId: files[0]?.driveId });
            if (file) return { ...newFile, ...file };
            throw new Error(error.message);
          }
          throw new Error(error.message);
        })
        .then((file) => {
          // update cache
          void mutate(`/drive/v3/files/${file.id}`, file, {
            revalidate: false,
            populateCache: true,
          });
          void mutate(
            `/drive/v3/files/${file.id}/export`,
            { html: '<html></html>' },
            {
              revalidate: false,
              populateCache: true,
            }
          );
          navigate(`/app/page/${wikiId}/${file.id}`);
          return [...files, file];
        })
        .finally(() => {
          setNewFile(null);
        });

      void mutate(`/api/files/${wikiId}`, request, {
        revalidate: false,
        populateCache: true,
        optimisticData: [...files, newFile],
      });
    }
  };
};

export const useOnMount = (cb: () => void) => {
  const onMount = useEffectEvent(cb);
  useEffect(() => {
    onMount();
  }, []);
};

export function usePathHistory() {
  const [location] = useLocation();
  const [paths, setPaths] = useState<{ current: string; previous: string | undefined }>({
    current: location,
    previous: undefined,
  });
  if (paths.current !== location) {
    setPaths({ current: location, previous: paths.current });
  }
  // console.log('paths:', paths);
  return paths;
}

type TreeMenuContextType = {
  addMenu: PopupState;
  moreMenu: PopupState;
};

export const TreeMenuContext = createContext<TreeMenuContextType | null>(null);

export const useTreeMenu = () => {
  const context = useContext(TreeMenuContext);
  if (!context) throw new Error('useTreeMenu must be used within TreeMenuContext.Provider');
  return context;
};
